import { useState, useEffect } from 'react';
import moment from 'moment';
import { useForm, FormProvider } from 'react-hook-form-v7';
import { DATE_FORMATS } from '../../../../utils/Application_Constants';
import usePatchInvoicesStatuses from '../../../hooks/usePatchInvoicesStatuses';
import { useInvoicingSelectionContext } from '../../../Contexts/InvoicingSelectionContext';
import formatDate from '../../../../utils/formatDate';
import InvoiceConfirmationModal from '../InvoiceConfirmationModal';

const initializeFormData = () => ({
    postingDate: moment(Date.now()),
    invoiceDate: moment(Date.now()),
    doNotSendToClient: false,
});

const PreInvoiceConfirmationModal = ({ open, onClose, action, onSuccess }) => {
    const [submissionError, setSubmissionError] = useState({ message: '', errors: [] });
    const { selectedBookingsCount, getSelectedInvoices } = useInvoicingSelectionContext();

    const formMethods = useForm({
        defaultValues: initializeFormData(),
        mode: 'onBlur',
    });

    useEffect(() => {
        if (open) {
            formMethods.reset(initializeFormData());
            setSubmissionError({ message: '', errors: [] });
        }
    }, [open, formMethods.reset]);

    const { mutate: patchStatuses, isLoading } = usePatchInvoicesStatuses({
        onMutate: () => {
            setSubmissionError({ message: '', errors: [] });
        },

        onSuccess: ({ successfulInvoiceIds = [], failedInvoices = [] }) => {
            const failedInvoiceReasons = failedInvoices.map(
                (failedInvoice) => failedInvoice.reason
            );

            let message = '';
            let type = 'success';
            let errors = [];
            const actionMessage = action === 'invoice' ? 'invoiced' : 'abandoned';
            if (successfulInvoiceIds.length > 0 && failedInvoices.length === 0) {
                message = `Pre-invoice(s) ${actionMessage} successfully!`;
            } else if (successfulInvoiceIds.length > 0 && failedInvoices.length > 0) {
                message = `${successfulInvoiceIds.length} pre-invoice(s) were ${actionMessage} successfully, but some failed:`;
                type = 'error';
                errors = failedInvoiceReasons;
            } else if (successfulInvoiceIds.length === 0 && failedInvoices.length > 0) {
                message = `All selected pre-invoice(s) could not be ${actionMessage}:`;
                type = 'error';
                errors = failedInvoiceReasons;
            }
            onSuccess?.({ message, type, errors });
            onClose();
        },
        onError: ({ error }) => {
            setSubmissionError({ message: error?.message, errors: error?.errors });
        },
    });

    const onSubmit = (formData) => {
        patchStatuses({
            invoiceIds: getSelectedInvoices().map((invoice) => invoice.bookingId),
            newStatus: action === 'invoice' ? 'Invoiced' : 'Abandoned',
            ...(action === 'invoice' && {
                doNotSendToClient: formData.doNotSendToClient,
                invoiceDate: formatDate(formData.invoiceDate.toDate(), {
                    format: DATE_FORMATS.ISO_8601,
                }),
                postingDate: formatDate(formData.postingDate.toDate(), {
                    format: DATE_FORMATS.ISO_8601,
                }),
            }),
        });
    };

    return (
        <FormProvider {...formMethods}>
            <InvoiceConfirmationModal
                open={open}
                onClose={onClose}
                title={`${action === 'invoice' ? 'Confirm' : 'Abandon'} Pre-invoices`}
                bodyMessage={`You've selected ${selectedBookingsCount} ${
                    action === 'invoice'
                        ? 'unbilled item(s).'
                        : 'pre-invoice(s) to abandon. This cannot be undone.'
                } Do you wish to proceed?`}
                showInvoiceForm={action === 'invoice'}
                onSubmit={formMethods.handleSubmit(onSubmit)}
                error={submissionError}
                isLoading={isLoading}
                actionButtonLabel={`${action === 'invoice' ? 'Confirm' : 'Abandon'} Pre-invoices`}
                actionButtonColor={action === 'invoice' ? 'primary' : 'error'}
            />
        </FormProvider>
    );
};

export default PreInvoiceConfirmationModal;
